import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { eventService } from "../services/authService";

const EventModal = ({ event, slot, user, onClose, onSave, onDelete }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    start: "",
    end: "",
    allDay: false,
    color: "#3174ad",
    type: "event",
    isPublic: false,
  });
  const [loading, setLoading] = useState(false);

  const eventTypes = [
    { value: "meeting", label: "🤝 Meeting", color: "#dc3545" },
    { value: "task", label: "✅ Task", color: "#28a745" },
    { value: "reminder", label: "⏰ Reminder", color: "#ffc107" },
    { value: "event", label: "📅 Event", color: "#3174ad" },
    { value: "other", label: "📝 Other", color: "#6c757d" },
  ];

  useEffect(() => {
    if (event) {
      // Editing existing event
      setFormData({
        title: event.title || "",
        description: event.description || "",
        start: moment(event.start).format("YYYY-MM-DDTHH:mm"),
        end: moment(event.end).format("YYYY-MM-DDTHH:mm"),
        allDay: event.allDay || false,
        color: event.color || "#3174ad",
        type: event.type || "event",
        isPublic: event.isPublic || false,
      });
    } else if (slot) {
      // Creating new event from selected slot
      setFormData({
        title: "",
        description: "",
        start: moment(slot.start).format("YYYY-MM-DDTHH:mm"),
        end: moment(slot.end).format("YYYY-MM-DDTHH:mm"),
        allDay: slot.allDay || false,
        color: "#3174ad",
        type: "event",
        isPublic: false,
      });
    }
  }, [event, slot]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleTypeChange = (selectedType) => {
    const typeConfig = eventTypes.find((t) => t.value === selectedType);
    setFormData((prev) => ({
      ...prev,
      type: selectedType,
      color: typeConfig?.color || "#3174ad",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate dates
      const startDate = new Date(formData.start);
      const endDate = new Date(formData.end);

      if (endDate <= startDate) {
        toast.error("End time must be after start time");
        setLoading(false);
        return;
      }

      const eventData = {
        ...formData,
        start: startDate.toISOString(),
        end: endDate.toISOString(),
      };

      if (event) {
        // Update existing event
        await eventService.updateEvent(event._id, eventData);
        toast.success("Event updated successfully");
      } else {
        // Create new event
        await eventService.createEvent(eventData);
        toast.success("Event created successfully");
      }

      onSave();
    } catch (error) {
      console.error("Error saving event:", error);
      toast.error(error.response?.data?.message || "Failed to save event");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (event && onDelete) {
      onDelete(event._id);
      onClose();
    }
  };

  const canEdit =
    !event || event.createdBy._id === user.id || user.role === "admin";
  const canDelete =
    event && (event.createdBy._id === user.id || user.role === "admin");

  return (
    <div className="modal-overlay">
      <div
        className="modal"
        style={{
          maxWidth: "600px",
          width: "90%",
          maxHeight: "90vh",
          overflowY: "auto",
          background: "linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%)",
          borderRadius: "20px",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
        }}
      >
        <div
          className="modal-header"
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "20px 20px 0 0",
            color: "white",
            padding: "25px 30px",
            borderBottom: "none",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2
            className="modal-title"
            style={{ margin: 0, fontSize: "24px", fontWeight: "600" }}
          >
            {event ? "📝 Edit Event" : "➕ Create New Event"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              border: "none",
              borderRadius: "50%",
              width: "35px",
              height: "35px",
              color: "white",
              fontSize: "18px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) =>
              (e.target.style.background = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.background = "rgba(255, 255, 255, 0.2)")
            }
          >
            ×
          </button>
        </div>

        {!canEdit ? (
          <div>
            <div className="modal-body" style={{ padding: "30px" }}>
              <div
                style={{
                  padding: "20px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                  marginBottom: "20px",
                }}
              >
                <h3>{event.title}</h3>
                {event.description && <p>{event.description}</p>}
                <div
                  style={{ marginTop: "10px", fontSize: "14px", color: "#666" }}
                >
                  <p>
                    <strong>Start:</strong> {moment(event.start).format("LLLL")}
                  </p>
                  <p>
                    <strong>End:</strong> {moment(event.end).format("LLLL")}
                  </p>
                  <p>
                    <strong>Type:</strong>{" "}
                    {eventTypes.find((t) => t.value === event.type)?.label ||
                      event.type}
                  </p>
                  <p>
                    <strong>Created by:</strong> {event.createdBy.username}
                  </p>
                  {event.isPublic && (
                    <p>
                      <strong>Visibility:</strong> Public
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal-body" style={{ padding: "30px" }}>
              <div className="form-group">
                <label className="form-label">Event Title *</label>
                <input
                  type="text"
                  name="title"
                  className="form-input"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter event title"
                  maxLength="100"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  className="form-input"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter event description (optional)"
                  rows="3"
                  maxLength="500"
                  style={{ resize: "vertical", minHeight: "80px" }}
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: "15px",
                }}
              >
                <div className="form-group">
                  <label className="form-label">Start Date & Time *</label>
                  <input
                    type="datetime-local"
                    name="start"
                    className="form-input"
                    value={formData.start}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">End Date & Time *</label>
                  <input
                    type="datetime-local"
                    name="end"
                    className="form-input"
                    value={formData.end}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Event Type</label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                    gap: "10px",
                  }}
                >
                  {eventTypes.map((type) => (
                    <label
                      key={type.value}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 12px",
                        backgroundColor:
                          formData.type === type.value ? type.color : "#f8f9fa",
                        color: formData.type === type.value ? "white" : "#333",
                        borderRadius: "6px",
                        cursor: "pointer",
                        border: `2px solid ${
                          formData.type === type.value ? type.color : "#e1e5e9"
                        }`,
                        transition: "all 0.2s ease",
                      }}
                    >
                      <input
                        type="radio"
                        name="type"
                        value={type.value}
                        checked={formData.type === type.value}
                        onChange={(e) => handleTypeChange(e.target.value)}
                        style={{ display: "none" }}
                      />
                      <span style={{ fontSize: "14px" }}>{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "15px",
                }}
              >
                <div className="form-group">
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      name="allDay"
                      checked={formData.allDay}
                      onChange={handleInputChange}
                    />
                    <span>All Day Event</span>
                  </label>
                </div>

                <div className="form-group">
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      name="isPublic"
                      checked={formData.isPublic}
                      onChange={handleInputChange}
                    />
                    <span>Make Public</span>
                  </label>
                </div>
              </div>
            </div>

            <div
              className="modal-footer"
              style={{
                padding: "20px 30px 30px",
                borderTop: "1px solid rgba(255, 255, 255, 0.2)",
                background: "rgba(255, 255, 255, 0.1)",
                borderRadius: "0 0 20px 20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <div>
                  {canDelete && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleDelete}
                      disabled={loading}
                    >
                      Delete Event
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : event
                      ? "Update Event"
                      : "Create Event"}
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EventModal;
